import {FC, useCallback, useEffect, useState} from 'react';

import {hideModal, showModal} from '@shared_web/components/core/modal';
import {showSuccess} from '@shared_web/components/core/notifications';
import {Pagination} from '@shared_web/components/core/pagination';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {icon_user_3_fill} from '@shared_web/components/icons/icon_user_3_fill';
import {notifyError} from '@shared_web/lib/notification';

import {CollaboratorForm} from '@mavismart_frontend/components/collaborator_form';
import {Line, TableWrapper} from '@mavismart_frontend/components/common_styles';
import {GenericConfirmModal} from '@mavismart_frontend/components/generic_confirm_modal';
import {InputSearch} from '@mavismart_frontend/components/input_search';
import {Loader} from '@mavismart_frontend/components/loader';
import {
  itemsCountLabel,
  itemsPerPageLabel,
  paginate,
  PAGINATION_MAX_LINES,
} from '@mavismart_frontend/components/pagination_utils';
import {translate, Translate} from '@mavismart_frontend/components/translate';
import {icon_delete} from '@mavismart_frontend/icons/icon_delete';
import {icon_plus_line} from '@mavismart_frontend/icons/icon_plus_line';
import {apiCall} from '@mavismart_frontend/lib/api';
import {
  CIRCLE_ICON_CONTAINER,
  LIGHT_GRAY_COLOR,
  MODAL_THEME,
  MODAL_TITLE_STYLE,
} from '@mavismart_frontend/lib/constants';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';

interface UserCollaboratorsProps {
  groupId: string;
}

export const UserCollaborators: FC<UserCollaboratorsProps> = ({groupId}) => {
  const account = useAccount();
  const locale = getUserLocale(account);
  const [collaborators, setCollaborators] = useState<string[]>();

  const [filteredCollaborators, setFilteredCollaborators] = useState<string[]>([]);
  const getLabel = useCallback((collaborator: string) => collaborator, []);

  const [currentPage, setCurrentPage] = useState(1);
  const paginatedCollaborators = paginate(filteredCollaborators, currentPage);

  const fetchCollaborators = useCallback(async () => {
    try {
      setCollaborators(undefined);
      const response = await apiCall('POST /list-collaborators', {groupId});
      setCollaborators(response.collaborators.sort());
    } catch (err) {
      notifyError(err);
    }
  }, [groupId]);

  useEffect(() => {
    fetchCollaborators().catch(notifyError);
  }, [fetchCollaborators]);

  const handleAddCollaborator = useCallback(() => {
    showModal({
      title: (
        <MODAL_TITLE_STYLE>
          <Translate value="add_a_new_collaborator" />
        </MODAL_TITLE_STYLE>
      ),
      ...MODAL_THEME,
      children: <CollaboratorForm groupId={groupId} fetchCollaborators={fetchCollaborators} />,
    });
  }, [groupId, fetchCollaborators]);

  const handleDeleteConfirm = useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      const collaboratorEmail = e.currentTarget.getAttribute('data-collaborator-email');

      if (collaboratorEmail === null) {
        console.error('Collaborator email not found');
        return;
      }
      try {
        await apiCall('POST /remove-collaborator', {email: collaboratorEmail, groupId});
      } catch (err) {
        notifyError(err);
      } finally {
        showSuccess(translate('collaborator_removed', getUserLanguage(account)));
        hideModal();
        fetchCollaborators().catch(notifyError);
      }
    },
    [fetchCollaborators, groupId, account]
  );

  const handleDeleteCollaborator = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      e.stopPropagation();
      const collaboratorEmail = e.currentTarget.getAttribute('data-collaborator-email');
      showModal({
        title: (
          <MODAL_TITLE_STYLE>
            {translate('confirmation_remove_collaborator', getUserLanguage(account))}
          </MODAL_TITLE_STYLE>
        ),
        ...MODAL_THEME,
        children: <GenericConfirmModal onConfirm={handleDeleteConfirm} data={collaboratorEmail} />,
      });
    },
    [account, handleDeleteConfirm]
  );

  if (!collaborators) {
    return <Loader />;
  }

  return (
    <>
      <Line>
        <InputSearch
          items={collaborators}
          setFilteredItems={setFilteredCollaborators}
          getLabel={getLabel}
        />
        <CIRCLE_ICON_CONTAINER onClick={handleAddCollaborator}>
          <SvgIcon icon={icon_plus_line} size={15} />
        </CIRCLE_ICON_CONTAINER>
      </Line>
      {filteredCollaborators.length === 0 ? (
        <Translate value="no_collaborator_found" />
      ) : (
        <TableWrapper>
          {paginatedCollaborators?.map(collaborator => (
            <GenericCell
              key={collaborator}
              id={collaborator}
              title={collaborator}
              icon={<SvgIcon size={24} icon={icon_user_3_fill} color={LIGHT_GRAY_COLOR} />}
              rightIcon={
                <SvgIcon
                  icon={icon_delete}
                  size={16}
                  color="transparent"
                  data-collaborator-email={collaborator}
                  onClick={handleDeleteCollaborator}
                />
              }
            />
          ))}
        </TableWrapper>
      )}
      <Pagination
        totalItems={filteredCollaborators.length}
        limit={PAGINATION_MAX_LINES}
        onPageChange={setCurrentPage}
        itemsCountLabel={itemsCountLabel}
        itemsPerPageLabel={itemsPerPageLabel}
        locale={locale}
      />
    </>
  );
};

UserCollaborators.displayName = 'UserCollaborators';
